import { useState, useRef, useEffect, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, LoadingSpinner, Modal, Input } from '@/components/ui'
import { kaTemplatesApi } from '@/lib/api'
import { gs1AiApi } from '@/lib/api/misc'
import type { KATemplate, KAAIEntry, GS1AIEntry } from '@/lib/api/misc'
import { useToast } from '@/hooks/useToast'
import { Plus, Pencil, Trash2, Box, Layers, Save, X, Copy, ChevronUp, ChevronDown } from 'lucide-react'

// --- Константы ---

const TEMPLATE_TYPES = [
  { value: 'ka_box' as const, label: 'Коробка', icon: Box },
  { value: 'ka_pallet' as const, label: 'Паллета', icon: Layers },
]

const AI_SOURCES = [
  { value: 'generated',      label: 'Авто-генерация (SSCC)' },
  { value: 'generated.ai21', label: 'Авто-генерация (серийник AI 21)' },
  { value: 'manual',         label: 'Ручной ввод' },
]

const DEFAULT_AI_BOX: KAAIEntry[] = [
  { ai: '00', label: 'SSCC коробки',     source: 'generated', required: true,  fnc1_after: true },
  { ai: '02', label: 'GTIN содержимого', source: 'manual',    required: false, fnc1_after: false },
  { ai: '37', label: 'Количество КМ',    source: 'manual',    required: false, fnc1_after: false },
]

const DEFAULT_AI_PALLET: KAAIEntry[] = [
  { ai: '00', label: 'SSCC паллеты',       source: 'generated', required: true,  fnc1_after: true },
  { ai: '02', label: 'GTIN содержимого',   source: 'manual',    required: false, fnc1_after: false },
  { ai: '37', label: 'Количество коробок', source: 'manual',    required: false, fnc1_after: false },
]

const DEFAULT_TEMPLATE_BOX: Partial<KATemplate> = {
  template_name: '',
  template_type: 'ka_box',
  description: '',
  extension_digit: '0',
  ai_list: DEFAULT_AI_BOX,
  fnc1_positions: [20],
  is_default: false,
  is_active: true,
}

// --- AI list editor ---

// Auto-source map: given a GS1 AI code, what source should be auto-selected?
function getAutoSource(aiCode: string): string {
  if (aiCode === '00') return 'generated'
  if (aiCode === '21') return 'generated.ai21'
  return 'manual'
}

function normalizeTemplateAIList(aiList: KAAIEntry[]): KAAIEntry[] {
  return aiList.map((entry) => {
    if (entry.ai === '00') return { ...entry, source: 'generated' }
    if (entry.ai === '21') return { ...entry, source: 'generated.ai21' }
    if (entry.source === 'generated' || entry.source === 'generated.ai21' || entry.source === 'manual') {
      return entry
    }
    return { ...entry, source: 'manual' }
  })
}

function normalizeTemplate(tpl: KATemplate): KATemplate {
  return { ...tpl, ai_list: normalizeTemplateAIList(tpl.ai_list ?? []) }
}

function AIListEditor({
  value,
  onChange,
  catalog,
}: {
  value: KAAIEntry[]
  onChange: (list: KAAIEntry[]) => void
  catalog: GS1AIEntry[]
  templateType?: string
}) {
  const [dropdownIdx, setDropdownIdx] = useState<number | null>(null)
  const [aiSearch, setAiSearch] = useState<Record<number, string>>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const newRowInputRef = useRef<HTMLInputElement | null>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownIdx(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Auto-focus AI input and open dropdown for new row
  useEffect(() => {
    if (newRowInputRef.current) {
      newRowInputRef.current.focus()
      newRowInputRef.current = null
    }
  })

  const addRow = () => {
    const newIdx = value.length
    onChange([...value, { ai: '', label: '', source: 'manual', required: false, fnc1_after: false, default_value: '' }])
    // Schedule dropdown open + focus for next render
    setTimeout(() => setDropdownIdx(newIdx), 0)
  }
  const updateRow = (idx: number, patch: Partial<KAAIEntry>) =>
    onChange(value.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const removeRow = (idx: number) => onChange(value.filter((_, i) => i !== idx))
  const moveRow = (idx: number, dir: -1 | 1) => {
    const next = idx + dir
    if (next < 0 || next >= value.length) return
    const arr = [...value]
    ;[arr[idx], arr[next]] = [arr[next], arr[idx]]
    onChange(arr)
  }

  const getFilteredAIs = (idx: number) => {
    const q = (aiSearch[idx] ?? value[idx]?.ai ?? '').toLowerCase()
    if (!q) return catalog.slice(0, 12)
    return catalog
      .filter(
        e =>
          e.ai.startsWith(q) ||
          e.title_ru.toLowerCase().includes(q) ||
          e.title_en.toLowerCase().includes(q),
      )
      .slice(0, 12)
  }

  const selectAI = (idx: number, entry: GS1AIEntry) => {
    const row = value[idx]
    const autoSource = getAutoSource(entry.ai)
    updateRow(idx, {
      ai: entry.ai,
      // auto-fill label if blank
      label: row.label || entry.title_ru,
      // auto-set fnc1_after based on GS1 catalog (variable-length fields need FNC1)
      fnc1_after: entry.fnc1_after,
      // auto-set source from known GS1 AI map (only if currently 'manual' or blank)
      ...(autoSource && (row.source === 'manual' || !row.source) ? { source: autoSource } : {}),
    })
    setAiSearch(prev => ({ ...prev, [idx]: entry.ai }))
    setDropdownIdx(null)
  }

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {value.length === 0 && (
        <p className="text-sm text-gray-400 italic py-2">Нет AI — нажмите + Добавить.</p>
      )}
      {value.map((row, idx) => (
        <Fragment key={idx}>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-200">
          <div className="flex flex-col gap-0.5 shrink-0">
            <button type="button" onClick={() => moveRow(idx, -1)} disabled={idx === 0}
              className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-25 text-gray-400">
              <ChevronUp className="h-3 w-3" />
            </button>
            <button type="button" onClick={() => moveRow(idx, 1)} disabled={idx === value.length - 1}
              className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-25 text-gray-400">
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>

          {/* AI code combobox */}
          <div className="relative shrink-0">
            <input
              className="w-20 rounded border border-gray-300 px-2 py-1.5 text-sm font-mono text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
              value={dropdownIdx === idx ? (aiSearch[idx] ?? row.ai) : row.ai}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                setAiSearch(prev => ({ ...prev, [idx]: v }))
                setDropdownIdx(idx)
                if (!v) updateRow(idx, { ai: '' })
              }}
              onFocus={() => {
                setAiSearch(prev => ({ ...prev, [idx]: row.ai }))
                setDropdownIdx(idx)
              }}
              onBlur={() => {
                // small delay so click on dropdown item registers first
                setTimeout(() => {
                  setDropdownIdx(prev => (prev === idx ? null : prev))
                }, 150)
              }}
              placeholder="AI"
              title="AI код — введите цифры или название для поиска"
              ref={row.ai === '' && dropdownIdx === idx ? newRowInputRef : undefined}
            />
            {dropdownIdx === idx && (
              <div className="absolute z-50 top-full left-0 mt-0.5 w-80 bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-56">
                {getFilteredAIs(idx).length === 0 ? (
                  <p className="px-3 py-2 text-xs text-gray-400">Ничего не найдено</p>
                ) : (
                  getFilteredAIs(idx).map(entry => (
                    <button
                      key={entry.ai}
                      type="button"
                      onMouseDown={e => { e.preventDefault(); selectAI(idx, entry) }}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center gap-2 group"
                    >
                      <span className="font-mono text-xs text-blue-700 w-10 shrink-0">{entry.ai}</span>
                      <span className="text-xs text-gray-700 truncate flex-1">{entry.title_ru}</span>
                      <span className="text-xs text-gray-400 font-mono shrink-0">{entry.format}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <input
            className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={row.label}
            onChange={e => updateRow(idx, { label: e.target.value })}
            placeholder="Название поля"
          />
          <select
            className="w-48 rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={row.source}
            onChange={e => updateRow(idx, { source: e.target.value })}
          >
            {AI_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-xs text-gray-500 shrink-0 cursor-pointer" title="Обязательный">
            <input
              type="checkbox"
              checked={row.required}
              onChange={e => updateRow(idx, { required: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 w-3.5 h-3.5"
            />
            Обяз.
          </label>
          <label className="flex items-center gap-1 text-xs shrink-0 cursor-pointer" title="Вставить FNC1 (Group Separator) после этого AI">
            <input
              type="checkbox"
              checked={row.fnc1_after ?? false}
              onChange={e => updateRow(idx, { fnc1_after: e.target.checked })}
              className="rounded border-gray-300 text-orange-500 w-3.5 h-3.5"
            />
            <span className={`${row.fnc1_after ? 'text-orange-600 font-medium' : 'text-gray-400'}`}>FNC1</span>
          </label>
          <button type="button" onClick={() => removeRow(idx)}
            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {/* Default value row — shown below AI row when source is manual */}
        {row.source === 'manual' && (
          <div className="flex items-center gap-2 ml-8 -mt-0.5 mb-1">
            <span className="text-xs text-gray-400 shrink-0">Значение по умолч.:</span>
            <input
              className="flex-1 min-w-0 rounded border border-dashed border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-gray-50"
              value={row.default_value ?? ''}
              onChange={e => updateRow(idx, { default_value: e.target.value })}
              placeholder="Не задано — будет запрашиваться при создании задания"
            />
          </div>
        )}
        </Fragment>
      ))}
      <button type="button" onClick={addRow}
        className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium mt-1">
        <Plus className="h-3.5 w-3.5" /> Добавить AI
      </button>
    </div>
  )
}

// --- Main Page ---

export function KATemplatesPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<Partial<KATemplate>>({ ...DEFAULT_TEMPLATE_BOX, ai_list: [...DEFAULT_AI_BOX] })
  const [filterType, setFilterType] = useState<'all' | 'ka_box' | 'ka_pallet'>('all')

  const { data: templatesResp, isLoading } = useQuery({
    queryKey: ['ka-templates'],
    queryFn: async () => {
      const { data } = await kaTemplatesApi.getAll()
      return data
    },
  })

  // GS1 AI Catalog — для комбобокса выбора AI
  const { data: gs1CatalogResp } = useQuery({
    queryKey: ['gs1-ai-catalog'],
    queryFn: async () => {
      const { data } = await gs1AiApi.getAll()
      return data
    },
    staleTime: Infinity, // каталог статичен
  })
  const gs1Catalog = (Array.isArray(gs1CatalogResp) ? gs1CatalogResp : gs1CatalogResp?.data) ?? []

  const templates: KATemplate[] = Array.isArray(templatesResp)
    ? templatesResp
    : (templatesResp as unknown as { data?: KATemplate[] })?.data ?? []

  const createMutation = useMutation({
    mutationFn: (data: Partial<KATemplate>) => kaTemplatesApi.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ka-templates'] }); toast.success('Шаблон КА создан'); closeModal() },
    onError: (err: Error) => toast.error(`Ошибка: ${err.message}`),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<KATemplate> }) => kaTemplatesApi.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ka-templates'] }); toast.success('Шаблон КА обновлён'); closeModal() },
    onError: (err: Error) => toast.error(`Ошибка: ${err.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => kaTemplatesApi.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ka-templates'] }); toast.success('Шаблон КА удалён') },
    onError: (err: Error) => toast.error(`Ошибка: ${err.message}`),
  })

  // Вычислить fnc1_positions из ai_list по длинам AI из GS1-каталога
  const computeFnc1Positions = (aiList: KAAIEntry[]): number[] => {
    let charPos = 0
    const positions: number[] = []
    for (const entry of aiList) {
      if (!entry.ai) continue
      // AI code length (may be 2, 3, or 4 digits)
      charPos += entry.ai.length
      // Field value length: look up in catalog first, fallback to heuristic
      const catalogEntry = gs1Catalog.find(c => c.ai === entry.ai)
      if (catalogEntry) {
        charPos += catalogEntry.fixed ?? catalogEntry.max_length ?? 0
      } else {
        // Fallback heuristic for common AIs
        charPos += entry.ai === '00' ? 18 : (entry.ai === '01' || entry.ai === '02') ? 14 : 6
      }
      if (entry.fnc1_after) {
        positions.push(charPos)
      }
    }
    return positions
  }

  const openCreate = () => {
    setEditingId(null)
    setForm({ ...DEFAULT_TEMPLATE_BOX, ai_list: [...DEFAULT_AI_BOX] })
    setModalOpen(true)
  }

  const openEdit = async (t: KATemplate) => {
    try {
      const { data } = await kaTemplatesApi.getById(t.template_id)
      const full = normalizeTemplate((data as unknown as { data?: KATemplate })?.data ?? (data as KATemplate))
      setEditingId(t.template_id)
      setForm(full)
      setModalOpen(true)
    } catch { toast.error('Не удалось загрузить шаблон') }
  }

  const openDuplicate = async (t: KATemplate) => {
    try {
      const { data } = await kaTemplatesApi.getById(t.template_id)
      const full = normalizeTemplate((data as unknown as { data?: KATemplate })?.data ?? (data as KATemplate))
      setEditingId(null)
      setForm({ ...full, template_id: undefined, template_name: `${full.template_name} (копия)`, is_default: false })
      setModalOpen(true)
    } catch { toast.error('Не удалось загрузить шаблон') }
  }

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Удалить шаблон КА "${name}"?`)) deleteMutation.mutate(id)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingId(null)
    setForm({ ...DEFAULT_TEMPLATE_BOX, ai_list: [...DEFAULT_AI_BOX] })
  }

  const handleTypeSwitch = (type: 'ka_box' | 'ka_pallet') => {
    const defaultAI = type === 'ka_pallet' ? DEFAULT_AI_PALLET : DEFAULT_AI_BOX
    const ext = type === 'ka_pallet' ? '1' : '0'
    setForm(f => ({ ...f, template_type: type, extension_digit: ext, ai_list: [...defaultAI] }))
  }

  const handleSave = () => {
    if (!form.template_name?.trim()) { toast.error('Укажите название шаблона'); return }
    // Вычисляем fnc1_positions из ai_list (fnc1_after флагов)
    const computedFnc1 = computeFnc1Positions(form.ai_list ?? [])
    const payload = { ...form, fnc1_positions: computedFnc1 }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const filteredTemplates = filterType === 'all' ? templates : templates.filter(t => t.template_type === filterType)

  if (isLoading) return <LoadingSpinner />

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Шаблоны КА</h1>
          <p className="text-sm text-gray-500 mt-1">
            GS1-структура кодов агрегации  Application Identifiers, FNC1, расширительная цифра SSCC
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4" /> Создать шаблон</Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {([
          { key: 'all' as const, label: 'Все' },
          { key: 'ka_box' as const, label: 'Коробки' },
          { key: 'ka_pallet' as const, label: 'Паллеты' },
        ]).map(tab => (
          <button key={tab.key}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterType === tab.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setFilterType(tab.key)}
          >
            {tab.label}
            <span className="ml-1.5 text-xs opacity-70">
              ({tab.key === 'all' ? templates.length : templates.filter(t => t.template_type === tab.key).length})
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-medium text-gray-600">Тип</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Название</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">ID</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Расш. цифра</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">AI состав</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">FNC1</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Активен</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredTemplates.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                  Нет шаблонов КА. Нажмите Создать шаблон.
                </td>
              </tr>
            ) : filteredTemplates.map(t => {
              const TypeIcon = t.template_type === 'ka_pallet' ? Layers : Box
              return (
                <tr key={t.template_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
                      t.template_type === 'ka_pallet' ? 'bg-purple-50 text-purple-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      <TypeIcon className="h-3.5 w-3.5" />
                      {t.template_type === 'ka_pallet' ? 'Паллета' : 'Коробка'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {t.template_name}
                    {t.is_default && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">по умолч.</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{t.template_id}</td>
                  <td className="px-4 py-3 text-center font-mono font-bold text-gray-700">
                    {t.extension_digit ?? <span className="text-gray-300"></span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(t.ai_list ?? []).map(a => (
                        <span key={a.ai} className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">({a.ai})</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {(t.fnc1_positions ?? []).length > 0
                      ? t.fnc1_positions.join(', ')
                      : <span className="text-gray-300"></span>
                    }
                  </td>
                  <td className="px-4 py-3 text-center">
                    {t.is_active
                      ? <span className="inline-flex h-2 w-2 rounded-full bg-green-500" />
                      : <span className="inline-flex h-2 w-2 rounded-full bg-gray-300" />
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button title="Редактировать" onClick={() => openEdit(t)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button title="Дублировать" onClick={() => openDuplicate(t)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-cyan-600 transition-colors">
                        <Copy className="h-4 w-4" />
                      </button>
                      <button title="Удалить" onClick={() => handleDelete(t.template_id, t.template_name)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={closeModal} className="max-w-2xl">
        <div className="px-6 pt-5 pb-6">
          <h2 className="text-lg font-semibold mb-5">
            {editingId ? 'Редактирование шаблона КА' : 'Новый шаблон КА'}
          </h2>

          <div className="space-y-5 max-h-[72vh] overflow-y-auto pr-1">

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Название *</label>
              <Input
                value={form.template_name ?? ''}
                onChange={e => setForm(f => ({ ...f, template_name: e.target.value }))}
                placeholder="Коробка стандартная GS1-128"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Тип агрегации</label>
              <div className="flex gap-2">
                {TEMPLATE_TYPES.map(tt => (
                  <button key={tt.value} type="button" onClick={() => handleTypeSwitch(tt.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      form.template_type === tt.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <tt.icon className="h-4 w-4" />{tt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
              <Input
                value={form.description ?? ''}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Для каких продуктов и когда применяется"
              />
            </div>

            {/* Extension digit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Расширительная цифра SSCC</label>
              <Input
                className="w-20"
                value={form.extension_digit ?? '0'}
                maxLength={1}
                onChange={e => setForm(f => ({ ...f, extension_digit: e.target.value.replace(/\D/g, '').slice(0, 1) }))}
                placeholder="0"
              />
              <p className="text-xs text-gray-400 mt-1">
                GS1: <span className="font-mono">0</span> = коробка, <span className="font-mono">1</span> = паллета.
                Первая цифра 18-значного SSCC.
              </p>
            </div>

            {/* AI List */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Состав кода  Application Identifiers (AI)
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Порядок строк = порядок AI в штрихкоде. AI <span className="font-mono">00</span> (SSCC) должен быть первым.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex gap-2 text-xs font-medium text-gray-500 mb-2 px-1">
                  <div className="w-5" />
                  <div className="w-20 text-center shrink-0">AI</div>
                  <div className="flex-1">Название</div>
                  <div className="w-48 shrink-0">Источник</div>
                  <div className="w-14 text-center shrink-0">Обяз.</div>
                  <div className="w-14 text-center shrink-0">FNC1</div>
                  <div className="w-6" />
                </div>
                <AIListEditor
                  value={form.ai_list ?? []}
                  onChange={list => setForm(f => ({ ...f, ai_list: list }))}
                  catalog={gs1Catalog}
                  templateType={form.template_type}
                />
              </div>
            </div>

            {/* FNC1 positions — авто-вычисляются из AI списка */}
            {(() => {
              const positions = computeFnc1Positions(form.ai_list ?? [])
              const fnc1Ais = (form.ai_list ?? []).filter(a => a.fnc1_after)
              return positions.length > 0 ? (
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5">
                  <p className="text-xs font-medium text-orange-700">
                    FNC1 / Group Separator (<span className="font-mono">\x1d</span>)
                  </p>
                  <p className="text-xs text-orange-600 mt-0.5">
                    Позиции: <span className="font-mono font-bold">{positions.join(', ')}</span>
                    {' '}— после AI: {fnc1Ais.map(a => `(${a.ai})`).join(', ')}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">FNC1 не вставляется — отметьте чекбокс «FNC1» у нужных AI в таблице выше.</p>
              )
            })()}

            {/* Flags */}
            <div className="flex gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_default ?? false}
                  onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600" />
                По умолчанию
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_active ?? true}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600" />
                Активен
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button variant="ghost" onClick={closeModal}><X className="h-4 w-4" /> Отмена</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              <Save className="h-4 w-4" />
              {editingId ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}